import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Sparkles, 
  Users, 
  Brain, 
  Heart, 
  ArrowRight,
  Star,
  Shield,
  Flame,
  Sun,
  Moon
} from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_IMAGE = "https://images.unsplash.com/photo-1754851539824-5a87c5c7cb86?crop=entropy&cs=srgb&fm=jpg&q=85";
const MEDITATION_IMAGE = "https://images.unsplash.com/photo-1577344718665-3e7c0c1ecf6b?crop=entropy&cs=srgb&fm=jpg&q=85";

const featuredArchetypes = [
  { id: "critico_interior", name: "El Crítico Interior", icon: Shield, color: "#C62828" },
  { id: "nino_herido", name: "El Niño Herido", icon: Heart, color: "#5C6BC0" },
  { id: "sabio", name: "El Sabio", icon: Star, color: "#7E57C2" },
  { id: "guerrero", name: "El Guerrero", icon: Flame, color: "#D32F2F" },
  { id: "self", name: "El Self", icon: Sun, color: "#FFC107" },
  { id: "sanador", name: "El Sanador", icon: Sparkles, color: "#26A69A" },
];

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Hero Background */}
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url('${HERO_IMAGE}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0518]/60 via-[#0F0518]/80 to-[#0F0518]" />

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white leading-tight mb-6">
              ¿Quiénes Viven{" "}
              <span className="text-secondary italic">Dentro de Ti</span>?
            </h1>
            <p className="font-body text-lg sm:text-xl text-primary-100 max-w-2xl mx-auto mb-4 leading-relaxed">
              Descubre tu ecosistema de arquetipos internos
            </p>
            <p className="font-body text-sm sm:text-base text-primary-300 max-w-xl mx-auto mb-10">
              Basado en Psicología Analítica de Jung, Internal Family Systems (IFS) 
              y neurociencia de la personalidad
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/test" data-testid="hero-cta-test">
                <Button 
                  size="lg" 
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg shadow-glow-primary transition-all hover:scale-105 w-full sm:w-auto"
                >
                  <Sparkles className="mr-2" size={20} />
                  Explorar Mis Arquetipos
                </Button>
              </Link>
              <Link to="/introduccion" data-testid="hero-cta-intro">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-secondary/50 text-secondary hover:bg-secondary/10 rounded-full px-8 py-6 text-lg w-full sm:w-auto"
                >
                  ¿Qué son los arquetipos?
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Floating elements */}
          <motion.div
            className="absolute top-20 left-10 text-primary/30"
            animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
          >
            <Moon size={40} />
          </motion.div>
          <motion.div
            className="absolute bottom-32 right-10 text-secondary/30"
            animate={{ y: [0, 15, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            <Star size={35} />
          </motion.div>
        </div>
      </section>

      {/* What You'll Discover Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
              Lo Que Descubrirás
            </h2>
            <p className="font-body text-primary-200 max-w-2xl mx-auto">
              Un viaje profundo hacia tu mundo interior, respaldado por ciencia y sabiduría ancestral
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Users, 
                title: "Tus 5-7 Arquetipos", 
                desc: "Identifica las sub-personalidades más activas en tu psique",
                color: "primary"
              },
              { 
                icon: Brain, 
                title: "Sus Interacciones", 
                desc: "Comprende cómo dialogan, colaboran o conflictuan entre sí",
                color: "secondary"
              },
              { 
                icon: Heart, 
                title: "Conflictos Internos", 
                desc: "Descubre las tensiones que generan bloqueos en tu vida",
                color: "accent"
              },
              { 
                icon: Sparkles, 
                title: "Prácticas IFS", 
                desc: "Recibe herramientas para armonizar tu ecosistema interior",
                color: "primary"
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card glass-card-hover rounded-xl p-6 text-center group"
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-4 transition-all group-hover:scale-110 ${
                  item.color === "primary" ? "bg-primary/20 text-primary-300" :
                  item.color === "secondary" ? "bg-secondary/20 text-secondary" :
                  "bg-accent/20 text-accent-glow"
                }`}>
                  <item.icon size={28} />
                </div>
                <h3 className="font-heading text-xl text-white mb-2">{item.title}</h3>
                <p className="font-body text-sm text-primary-200">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Archetypes */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary-950/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
              Los 15 Arquetipos Universales
            </h2>
            <p className="font-body text-primary-200 max-w-2xl mx-auto">
              Cada uno cumple una función en tu psique. No son "buenos" ni "malos", 
              solo necesitan ser vistos, comprendidos y armonizados.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {featuredArchetypes.map((arch, index) => (
              <motion.div
                key={arch.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/arquetipo/${arch.id}`}
                  className="glass-card glass-card-hover rounded-xl p-4 text-center block group h-full"
                  data-testid={`archetype-card-${arch.id}`}
                >
                  <div 
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3 transition-all group-hover:scale-110"
                    style={{ backgroundColor: `${arch.color}20`, color: arch.color }}
                  >
                    <arch.icon size={24} />
                  </div>
                  <p className="font-body text-sm text-primary-100 group-hover:text-white transition-colors">
                    {arch.name}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/biblioteca" data-testid="view-all-archetypes">
              <Button variant="outline" className="border-secondary/50 text-secondary hover:bg-secondary/10 rounded-full">
                Ver todos los arquetipos
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <blockquote className="glass-card rounded-xl p-8 mb-8 border-l-4 border-secondary">
                <p className="font-heading text-xl sm:text-2xl text-primary-100 italic mb-4">
                  "No me he convertido en lo que soy luchando contra lo que no quiero ser, 
                  sino abrazando todas las partes de mí mismo."
                </p>
                <footer className="font-body text-sm text-primary-300">
                  — Carl Gustav Jung
                </footer>
              </blockquote>

              <div className="space-y-4 text-primary-200 font-body">
                <p>
                  Los arquetipos no son "personalidades múltiples" ni patología. 
                  Son <strong className="text-white">voces naturales de tu psique</strong>, 
                  cada una con una función evolutiva.
                </p>
                <p>
                  El problema no es tenerlas, sino estar <strong className="text-white">fusionado</strong> con ellas — 
                  creer que ERES el Crítico, el Herido, el Protector.
                </p>
                <p>
                  El camino de individuación es aprender a escuchar todas las voces con compasión, 
                  honrar sus dones, integrar sus sombras, y actuar desde un centro de consciencia más amplio: 
                  <strong className="text-secondary"> el Self</strong>.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="relative">
                <img 
                  src={MEDITATION_IMAGE} 
                  alt="Meditación y autoconocimiento"
                  className="rounded-xl w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-[#0F0518] via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center glass-card rounded-2xl p-8 sm:p-12"
        >
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
            ¿Listo para Conocer tu Ecosistema Interior?
          </h2>
          <p className="font-body text-primary-200 mb-8 max-w-xl mx-auto">
            El test toma aproximadamente 15-20 minutos. Es gratuito, sin registro, 
            y recibirás un reporte personalizado completo.
          </p>
          <Link to="/test" data-testid="final-cta-test">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-6 text-lg shadow-glow-primary transition-all hover:scale-105"
            >
              <Sparkles className="mr-2" size={20} />
              Comenzar el Test
            </Button>
          </Link>
          <p className="font-body text-xs text-primary-400 mt-6">
            100% gratuito • Sin registro • Resultados inmediatos
          </p>
        </motion.div>
      </section>
    </div>
  );
}
